import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { customersAPI } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import './CustomerForm.css';

const SERVICE_OPTIONS = [
  { value: 'MandatPro', label: 'MandatPro' },
  { value: 'HandwerksPro', label: 'HandwerksPro' },
  { value: 'LVR Site', label: 'LVR Site' },
  { value: 'Other', label: 'Sonstiges' },
];

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: 'Deutschland',
  services: [],
  monthly_fee: '',
  contract_start_date: '',
  notes: '',
  active: true,
};

const CustomerForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await customersAPI.getById(id);
        if (cancelled) return;
        setForm({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address_line1: data.address_line1 || '',
          address_line2: data.address_line2 || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
          country: data.country || 'Deutschland',
          services: Array.isArray(data.services) ? data.services : [],
          monthly_fee:
            data.monthly_fee !== null && data.monthly_fee !== undefined
              ? String(data.monthly_fee)
              : '',
          contract_start_date: data.contract_start_date || '',
          notes: data.notes || '',
          active: data.active !== false,
        });
      } catch (err) {
        toast.error(
          err.response?.status === 404
            ? 'Kunde nicht gefunden'
            : 'Fehler beim Laden des Kunden'
        );
        navigate('/customers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const toggleService = (service) => {
    setForm((prev) => {
      const has = prev.services.includes(service);
      return {
        ...prev,
        services: has
          ? prev.services.filter((s) => s !== service)
          : [...prev.services, service],
      };
    });
  };

  const validate = () => {
    const e = {};

    if (!form.name.trim()) {
      e.name = 'Name ist erforderlich';
    } else if (form.name.trim().length < 2) {
      e.name = 'Name muss mindestens 2 Zeichen lang sein';
    }

    if (form.email.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        e.email = 'Ungültige E-Mail-Adresse';
      }
    }

    if (form.phone.trim() && !/^[\d\s+\-()/.]{5,30}$/.test(form.phone.trim())) {
      e.phone = 'Ungültige Telefonnummer';
    }

    if (!form.address_line1.trim()) {
      e.address_line1 = 'Straße ist erforderlich';
    }

    if (!form.postal_code.trim()) {
      e.postal_code = 'PLZ ist erforderlich';
    } else if (!/^\d{4,5}$/.test(form.postal_code.trim())) {
      e.postal_code = 'PLZ muss 4–5 Ziffern enthalten';
    }

    if (!form.city.trim()) {
      e.city = 'Ort ist erforderlich';
    }

    if (form.monthly_fee !== '' && form.monthly_fee !== null) {
      const n = Number(String(form.monthly_fee).replace(',', '.'));
      if (!Number.isFinite(n) || n < 0) {
        e.monthly_fee = 'Ungültiger Betrag';
      }
    }

    if (form.contract_start_date) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.contract_start_date)) {
        e.contract_start_date = 'Ungültiges Datum';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Bitte korrigieren Sie die markierten Felder');
      return;
    }

    setSaving(true);
    const monthlyFeeNum =
      form.monthly_fee === '' || form.monthly_fee === null
        ? 0
        : Number(String(form.monthly_fee).replace(',', '.'));

    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address_line1: form.address_line1.trim(),
      address_line2: form.address_line2.trim() || null,
      postal_code: form.postal_code.trim(),
      city: form.city.trim(),
      country: form.country.trim() || 'Deutschland',
      services: form.services,
      monthly_fee: monthlyFeeNum,
      contract_start_date: form.contract_start_date || null,
      notes: form.notes.trim() || null,
      active: form.active,
    };

    try {
      if (isEdit) {
        await customersAPI.update(id, payload);
        toast.success('Kunde aktualisiert');
      } else {
        await customersAPI.create(payload);
        toast.success('Kunde angelegt');
      }
      navigate('/customers');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        const fieldErrors = {};
        detail.forEach((d) => {
          const field = d.loc?.[d.loc.length - 1];
          if (field) fieldErrors[field] = d.msg;
        });
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        toast.error('Bitte Eingaben prüfen');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="content-header">
        <div>
          <Link to="/customers" className="back-link">
            <ArrowLeft size={16} /> Zurück zu Kunden
          </Link>
          <h1>{isEdit ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h1>
          <p>
            {isEdit
              ? 'Aktualisieren Sie die Kundendaten'
              : 'Legen Sie einen neuen Kunden an'}
          </p>
        </div>
      </div>

      <div className="content-body">
        <div className="customer-form-grid">
          <section className="card">
            <div className="card-header">
              <h2>Stammdaten</h2>
            </div>
            <div className="card-body form-section">
              <div className="form-group">
                <label className="form-label" htmlFor="name">
                  Name (Firma oder Person) *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  className={`form-input ${errors.name ? 'is-invalid' : ''}`}
                  maxLength={255}
                  autoComplete="organization"
                />
                {errors.name && <p className="field-error">{errors.name}</p>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    E-Mail
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? 'is-invalid' : ''}`}
                    autoComplete="email"
                  />
                  {errors.email && (
                    <p className="field-error">{errors.email}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="phone">
                    Telefon
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className={`form-input ${errors.phone ? 'is-invalid' : ''}`}
                    placeholder="+49 ..."
                    autoComplete="tel"
                  />
                  {errors.phone && (
                    <p className="field-error">{errors.phone}</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2>Anschrift</h2>
            </div>
            <div className="card-body form-section">
              <div className="form-group">
                <label className="form-label" htmlFor="address_line1">
                  Straße und Hausnummer *
                </label>
                <input
                  id="address_line1"
                  name="address_line1"
                  type="text"
                  value={form.address_line1}
                  onChange={handleChange}
                  className={`form-input ${
                    errors.address_line1 ? 'is-invalid' : ''
                  }`}
                  maxLength={255}
                  autoComplete="address-line1"
                />
                {errors.address_line1 && (
                  <p className="field-error">{errors.address_line1}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="address_line2">
                  Adresszusatz (optional)
                </label>
                <input
                  id="address_line2"
                  name="address_line2"
                  type="text"
                  value={form.address_line2}
                  onChange={handleChange}
                  className="form-input"
                  maxLength={255}
                  autoComplete="address-line2"
                />
              </div>

              <div className="form-row form-row-3">
                <div className="form-group postal-code-group">
                  <label className="form-label" htmlFor="postal_code">
                    PLZ *
                  </label>
                  <input
                    id="postal_code"
                    name="postal_code"
                    type="text"
                    value={form.postal_code}
                    onChange={handleChange}
                    className={`form-input ${
                      errors.postal_code ? 'is-invalid' : ''
                    }`}
                    maxLength={20}
                    autoComplete="postal-code"
                    inputMode="numeric"
                  />
                  {errors.postal_code && (
                    <p className="field-error">{errors.postal_code}</p>
                  )}
                </div>

                <div className="form-group city-group">
                  <label className="form-label" htmlFor="city">
                    Ort *
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={form.city}
                    onChange={handleChange}
                    className={`form-input ${errors.city ? 'is-invalid' : ''}`}
                    maxLength={100}
                    autoComplete="address-level2"
                  />
                  {errors.city && <p className="field-error">{errors.city}</p>}
                </div>

                <div className="form-group country-group">
                  <label className="form-label" htmlFor="country">
                    Land
                  </label>
                  <input
                    id="country"
                    name="country"
                    type="text"
                    value={form.country}
                    onChange={handleChange}
                    className="form-input"
                    maxLength={100}
                    autoComplete="country-name"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2>Service &amp; Vertrag</h2>
            </div>
            <div className="card-body form-section">
              <div className="form-group">
                <label className="form-label">Genutzte Services</label>
                <div className="service-checkboxes">
                  {SERVICE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="service-checkbox">
                      <input
                        type="checkbox"
                        checked={form.services.includes(opt.value)}
                        onChange={() => toggleService(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="monthly_fee">
                    Monatliche Gebühr (€)
                  </label>
                  <input
                    id="monthly_fee"
                    name="monthly_fee"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.monthly_fee}
                    onChange={handleChange}
                    className={`form-input ${
                      errors.monthly_fee ? 'is-invalid' : ''
                    }`}
                    placeholder="0.00"
                    inputMode="decimal"
                  />
                  {errors.monthly_fee && (
                    <p className="field-error">{errors.monthly_fee}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="contract_start_date">
                    Vertragsbeginn
                  </label>
                  <input
                    id="contract_start_date"
                    name="contract_start_date"
                    type="date"
                    value={form.contract_start_date}
                    onChange={handleChange}
                    className={`form-input ${
                      errors.contract_start_date ? 'is-invalid' : ''
                    }`}
                  />
                  {errors.contract_start_date && (
                    <p className="field-error">{errors.contract_start_date}</p>
                  )}
                </div>
              </div>

              {isEdit && (
                <div className="form-group">
                  <label className="service-checkbox">
                    <input
                      type="checkbox"
                      name="active"
                      checked={form.active}
                      onChange={handleChange}
                    />
                    <span>Kunde ist aktiv</span>
                  </label>
                </div>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2>Notizen</h2>
            </div>
            <div className="card-body form-section">
              <div className="form-group">
                <label className="form-label" htmlFor="notes">
                  Interne Notizen
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  className="form-input"
                  rows={4}
                  placeholder="Optionale interne Anmerkungen zum Kunden..."
                />
              </div>
            </div>
          </section>
        </div>

        <div className="form-actions">
          <Link
            to="/customers"
            className="btn btn-outline"
            aria-disabled={saving}
          >
            Abbrechen
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <LoadingSpinner size="small" />
                Speichere...
              </>
            ) : (
              <>
                <Save size={16} />
                {isEdit ? 'Änderungen speichern' : 'Kunde anlegen'}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CustomerForm;
